import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { useEffect } from 'react';

interface MetroMapProps {
  children?: React.ReactNode;
  center?: LatLngExpression;
  zoom?: number;
  bounds?: LatLngBoundsExpression;
  className?: string;
  onClick?: (lat: number, lng: number) => void;
}

// Component to fit map to bounds
function FitBounds({ bounds }: { bounds?: LatLngBoundsExpression }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
}

// Component to handle map clicks
function MapClickHandler({ onClick }: { onClick?: (lat: number, lng: number) => void }) {
  const map = useMap();

  useEffect(() => {
    if (!onClick) return;

    const handleClick = (e: L.LeafletMouseEvent) => {
      onClick(e.latlng.lat, e.latlng.lng);
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onClick]);

  return null;
}

export function MetroMap({
  children,
  center = [39.0, -94.5], // Default to Kansas City area
  zoom = 10,
  bounds,
  className = '',
  onClick
}: MetroMapProps) {
  return (
    <MapContainer
      center={center}
      zoom={zoom}
      className={`w-full h-full min-h-[400px] ${className}`}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds bounds={bounds} />
      <MapClickHandler onClick={onClick} />
      {children}
    </MapContainer>
  );
}
